import React, { useEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserId } from '@common-reducers/UserSelectors';
import { useRouter } from '@common-reducers/hooks/use-router';
import { useSafeLocation } from '@common/services/utils';
import {
  useGetAgentDay1PromptStateQuery,
  useRecordAgentDay1PromptMutation,
} from '@common-reducers/backend-api/backend-api';
import { BRAND, brandCtaCss } from '@common/styles/brand-tokens';
import { Mascot } from '@common/components/mascot/Mascot';
import { NEW_AGENT_PATH } from '@/pages/my-bridge/workspace-view';
import { pageLoadFlagsFor, shouldRenderAgentDay1Prompt } from './agent-day1-prompt-logic';

const STYLES = `
.bt-ad1{animation:bt-ad1-in .35s cubic-bezier(.2,.8,.2,1) both}
@keyframes bt-ad1-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
${brandCtaCss('bt-ad1-cta')}
.bt-ad1-x,.bt-ad1-later{transition:background .15s ease,color .15s ease}
.bt-ad1-x:hover,.bt-ad1-later:hover{background:${BRAND.track};color:${BRAND.text}!important}
.bt-ad1-x:focus-visible,.bt-ad1-later:focus-visible{outline:2px solid ${BRAND.accent};outline-offset:2px}
@media (prefers-reduced-motion:reduce){.bt-ad1{animation:none}}
`;

/**
 * #603 agent-day-1 A/B prompt (web only). Renders nothing unless the server says this user is
 * in the treatment arm and the prompt is due; control, unassigned, flag off and any error all
 * render nothing. One click opens the agent builder.
 */
const AgentDay1Prompt: React.FC = () => {
  const { t, i18n } = useTranslation(namespace);
  const { navigate } = useRouter();
  const { pathname } = useSafeLocation();
  const userId = useAppSelector(selectUserId);
  const { data } = useGetAgentDay1PromptStateQuery(undefined, { skip: !userId });
  const [record] = useRecordAgentDay1PromptMutation();
  const flags = pageLoadFlagsFor(userId);
  const [, setClosed] = useState(false);

  const visible = shouldRenderAgentDay1Prompt({ state: data, pathname, closed: flags.closed, shownThisLoad: flags.shown });

  // Exposure is recorded when the prompt actually renders, not when the state loads.
  useEffect(() => {
    if (!visible || flags.shown) return;
    flags.shown = true;
    // A failed call is retried on the next mount rather than lost from the exposure count.
    record('shown').unwrap().catch(() => { flags.shown = false; });
  }, [visible, record, flags]);

  if (!visible) return null;

  const close = (op: 'clicked' | 'dismissed') => {
    flags.closed = true;
    setClosed(true);
    record(op).unwrap().catch(() => undefined);
  };

  return (
    <div
      className="bt-ad1"
      role="dialog"
      aria-labelledby="bt-ad1-title"
      dir={i18n.dir()}
      style={{
        // Sits above the support-chat launcher in the same corner, so it never hides it.
        position: 'fixed', bottom: 92, insetInlineEnd: 20, zIndex: 1300,
        width: 'min(360px, calc(100vw - 32px))', boxSizing: 'border-box',
        background: BRAND.surface, color: BRAND.text, border: `1px solid ${BRAND.border}`,
        borderRadius: 14, boxShadow: '0 12px 32px rgba(15,23,42,.16)', padding: 18,
      }}
    >
      <style>{STYLES}</style>
      <button
        type="button"
        className="bt-ad1-x"
        onClick={() => close('dismissed')}
        aria-label={t('agentDay1Prompt.close')}
        style={{
          position: 'absolute', top: 10, insetInlineEnd: 10, width: 30, height: 30, borderRadius: 8,
          border: 'none', background: 'transparent', color: BRAND.muted, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}
      >
        <X size={16} strokeWidth={2.25} />
      </button>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingInlineEnd: 24 }}>
        <span style={{ flex: '0 0 auto' }}><Mascot size={52} mood="happy" level="ambient" /></span>
        <div style={{ minWidth: 0 }}>
          <h3 id="bt-ad1-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
            {t('agentDay1Prompt.title')}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.5, color: BRAND.muted }}>
            {t('agentDay1Prompt.body')}
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="bt-ad1-later"
          onClick={() => close('dismissed')}
          style={{
            background: 'transparent', color: BRAND.muted, border: 'none', fontSize: 13, fontWeight: 600,
            height: 36, padding: '0 14px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {t('agentDay1Prompt.later')}
        </button>
        <button
          type="button"
          className="bt-ad1-cta"
          onClick={() => {
            close('clicked');
            navigate(NEW_AGENT_PATH);
          }}
          style={{
            background: BRAND.brand, color: BRAND.brandText, border: 'none', fontSize: 13, fontWeight: 600,
            height: 36, padding: '0 16px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: BRAND.brandShadow, whiteSpace: 'nowrap',
          }}
        >
          {t('agentDay1Prompt.cta')}
          <ArrowRight size={15} strokeWidth={2.5} style={{ transform: i18n.dir() === 'rtl' ? 'scaleX(-1)' : undefined }} />
        </button>
      </div>
    </div>
  );
};

export default AgentDay1Prompt;
